import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowBack } from '@mui/icons-material';
import { Button, ContextualNavigationLayout, ScrollableView } from '@uplift/ui-v2';
import { IdealPreview } from './IdealPreview.component';
import { ProfileFueSteps, matchingProfileFueSteps } from './ProfileFueSteps.component';
import { PROFILE_FUE_STATUS_QUERY_KEY, PROFILE_ROW_QUERY_KEYS, usePreprocessProfileEditKeyChange } from '../profile.utils';
import { useEffect } from 'react';
import { MatchingProfileRow } from '@uplift/types';
import { ProviderMatchingProfileBookmark, patchProviderDetails } from '@uplift/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useProfileContext } from '../profile.context';
import { useHistory } from 'react-router';

export type ProfileFueProps = {
  status: Exclude<ProviderMatchingProfileBookmark, ProviderMatchingProfileBookmark.complete>;
  onComplete?: () => void;
};

export const ProfileFue: React.VFC<ProfileFueProps> = props => {
  const history = useHistory();
  const { profile } = useProfileContext();
  const prefetch = usePreprocessProfileEditKeyChange();
  const { classes } = useProfileFueStyles();
  const queryClient = useQueryClient();
  const startFueStepsMigration = useMutation({
    mutationFn: async () => {
      patchProviderDetails(profile.user.id, { matchingProfileBookmark: matchingProfileFueSteps[0] });

      queryClient.setQueryData<ProviderMatchingProfileBookmark>(PROFILE_FUE_STATUS_QUERY_KEY(profile.user.id), matchingProfileFueSteps[0]);
    }
  });

  // prefetch data for all steps
  useEffect(() => {
    const entries = Object.keys(PROFILE_ROW_QUERY_KEYS) as MatchingProfileRow[];

    entries.forEach(prefetch);
  }, []);

  if (props.status === ProviderMatchingProfileBookmark.initial) {
    return (
      <ContextualNavigationLayout
        headerProps={{ leftButton: <Button startIcon={<ArrowBack />} onClick={() => history.goBack()}>Back</Button> }}
      >
        <ScrollableView>
          <Box display="flex">
            <Box flex={1} display="flex" justifyContent="center">
              <Box className={classes.getStartedContainer}>
                <Box>
                  <Typography variant="overline">Create Your</Typography>
                  <Typography variant="h2">Matching Profile</Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap="1rem">
                  <Typography>
                    Your matching profile is an important tool for your success as a therapist on UpLift.
                  </Typography>
                  <Typography>
                    To Build a great profile will require time and thought. Don't worry – you'll have plenty of time and we'll help you through the process with tips on how to make an effective profile.
                  </Typography>
                </Box>
                <Box>
                  <Button variant="contained" color="secondary" onClick={() => startFueStepsMigration.mutate()}>Get Started</Button>
                </Box>
              </Box>
            </Box>
            <Box flex={1}>
              <IdealPreview />
            </Box>
          </Box>
        </ScrollableView>
      </ContextualNavigationLayout>
    );
  }

  return <ProfileFueSteps status={props.status} />;
};

const useProfileFueStyles = makeStyles({ name: 'ProfileFue' })(theme => ({
  getStartedContainer: {
    position: 'fixed',
    top: 'calc(50% - 64px)',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    maxWidth: '500px',
  },
}));
